import { ScaffoldModule, Question, ScaffoldConfig, InstallResult, FileChange, Framework, FileOperations } from '@scaffit/core';
import { join } from 'path';

// Standalone usage functions for direct npm package usage
// Note: Requires @scaffit/core to be installed
export async function setupTypeScript(options: {
  strictnessLevel?: 'recommended' | 'strict' | 'custom';
  includePathMapping?: boolean;
  includeBuildScripts?: boolean;
  projectRoot?: string;
} = {}): Promise<InstallResult> {
  const config: ScaffoldConfig = {
    strictnessLevel: options.strictnessLevel ?? 'recommended',
    includePathMapping: options.includePathMapping ?? true,
    includeBuildScripts: options.includeBuildScripts ?? true,
    projectInfo: {
      rootDir: options.projectRoot || process.cwd()
    }
  };
  
  return scaffold.install(config);
}

export async function previewTypeScript(options: {
  strictnessLevel?: 'recommended' | 'strict' | 'custom';
  includePathMapping?: boolean;
  includeBuildScripts?: boolean;
} = {}): Promise<FileChange[]> {
  const config: ScaffoldConfig = {
    strictnessLevel: options.strictnessLevel ?? 'recommended',
    includePathMapping: options.includePathMapping ?? true,
    includeBuildScripts: options.includeBuildScripts ?? true
  };
  
  return scaffold.getPreview!(config);
}

// CLI Integration (ScaffoldModule)
const scaffold: ScaffoldModule = {
  name: 'typescript',
  description: 'TypeScript configuration setup with strict configs',
  category: 'Foundation',
  tags: ['typescript', 'tsconfig', 'strict-config', 'type-checking', 'nextjs', 'react', 'vue', 'angular', 'svelte', 'express', 'fastify', 'nodejs'],

  prompts: async (): Promise<Question[]> => [
    {
      type: 'select',
      name: 'strictnessLevel',
      message: 'Choose TypeScript strictness level:',
      choices: [
        'recommended',
        'strict',
        'custom'
      ],
      default: 'recommended'
    },
    {
      type: 'confirm',
      name: 'enablePathMapping',
      message: 'Enable path mapping (@/* aliases)?',
      default: true
    },
    {
      type: 'confirm',
      name: 'addTypeScriptScripts',
      message: 'Add TypeScript scripts to package.json?',
      default: true
    },
    {
      type: 'confirm',
      name: 'addNodeTypes',
      message: 'Add @types/node for Node.js projects?',
      default: true
    }
  ],

  install: async (config: ScaffoldConfig): Promise<InstallResult> => {
    const result: InstallResult = {
      success: true,
      filesChanged: [],
      dependencies: { dev: [], prod: [] },
      errors: [],
      warnings: []
    };

    try {
      const projectRoot = config.projectInfo?.rootDir || process.cwd();
      const framework = config.framework || 'nodejs' as Framework;

      // Create tsconfig.json file
      const tsconfigPath = join(projectRoot, 'tsconfig.json');
      const tsconfig = getFrameworkSpecificConfig(framework, config);

      await FileOperations.writeFile(tsconfigPath, JSON.stringify(tsconfig, null, 2));
      result.filesChanged.push({
        action: 'created',
        path: 'tsconfig.json',
        content: JSON.stringify(tsconfig, null, 2)
      });

      // Update package.json
      const packageJsonPath = join(projectRoot, 'package.json');
      if (await FileOperations.fileExists(packageJsonPath)) {
        const packageJsonContent = await FileOperations.readFile(packageJsonPath);
        if (!packageJsonContent) {
          result.errors.push('Failed to read package.json');
          return result;
        }
        
        const packageJson = JSON.parse(packageJsonContent);
        
        // Add dependencies
        if (!packageJson.devDependencies) {
          packageJson.devDependencies = {};
        }
        
        // Add TypeScript
        if (!packageJson.devDependencies.typescript) {
          packageJson.devDependencies.typescript = '^5.3.0';
          result.dependencies.dev.push('typescript');
        }

        // Add @types/node for Node.js projects
        if (config.addNodeTypes && (framework === 'express' || framework === 'fastify' || framework === 'nodejs')) {
          if (!packageJson.devDependencies['@types/node']) {
            packageJson.devDependencies['@types/node'] = '^20.0.0';
            result.dependencies.dev.push('@types/node');
          }
        }

        // Add scripts
        if (config.addTypeScriptScripts && !packageJson.scripts) {
          packageJson.scripts = {};
        }
        
        if (config.addTypeScriptScripts) {
          if (!packageJson.scripts['type-check']) {
            packageJson.scripts['type-check'] = 'tsc --noEmit';
          }
          
          // Add build scripts for Node.js projects
          if (framework === 'express' || framework === 'fastify' || framework === 'nodejs') {
            if (!packageJson.scripts.build) {
              packageJson.scripts.build = 'tsc';
            }
            if (!packageJson.scripts.dev) {
              packageJson.scripts.dev = 'tsc --watch';
            }
          }
        }

        await FileOperations.writeFile(packageJsonPath, JSON.stringify(packageJson, null, 2));
        result.filesChanged.push({
          action: 'modified',
          path: 'package.json',
          content: JSON.stringify(packageJson, null, 2)
        });
      }

      result.success = result.errors.length === 0;
      return result;

    } catch (error) {
      result.success = false;
      result.errors.push(error instanceof Error ? error.message : 'Unknown error');
      return result;
    }
  },

  getPreview: async (config: ScaffoldConfig): Promise<FileChange[]> => {
    const changes: FileChange[] = [];

    // Preview tsconfig.json
    const tsconfig = getFrameworkSpecificConfig('nodejs', config);

    changes.push({
      action: 'created',
      path: 'tsconfig.json',
      content: JSON.stringify(tsconfig, null, 2)
    });

    // Preview package.json changes
    const dependencies = ['typescript'];
    if (config.addNodeTypes) {
      dependencies.push('@types/node');
    }

    changes.push({
      action: 'modified',
      path: 'package.json',
      content: dependencies.map(dep => `+ "${dep}": "^latest"`).join('\n') + 
            (config.addTypeScriptScripts ? '\n+ "type-check": "tsc --noEmit"\n+ "build": "tsc"' : '')
    });

    return changes;
  }
};

// Helper functions
function getFrameworkSpecificConfig(framework: Framework, config: ScaffoldConfig): any {
  const baseConfig = {
    compilerOptions: {
      target: 'ES2022',
      allowJs: true,
      skipLibCheck: true,
      strict: config.strictnessLevel !== 'custom',
      esModuleInterop: true,
      resolveJsonModule: true,
      isolatedModules: true,
      incremental: true,
      ...getStrictnessOptions(config.strictnessLevel)
    },
    include: [],
    exclude: ['node_modules']
  };

  // Framework-specific configurations
  switch (framework) {
    case 'nextjs':
      return {
        ...baseConfig,
        compilerOptions: {
          ...baseConfig.compilerOptions,
          lib: ['dom', 'dom.iterable', 'es6'],
          noEmit: true,
          module: 'esnext',
          moduleResolution: 'bundler',
          jsx: 'preserve',
          plugins: [{ name: 'next' }],
          ...(config.enablePathMapping && {
            baseUrl: '.',
            paths: FileOperations.getDynamicPathMappings(config.projectInfo || { framework, rootDir: process.cwd() })
          })
        },
        include: ['next-env.d.ts', '**/*.ts', '**/*.tsx', '.next/types/**/*.ts'],
        exclude: ['node_modules']
      };
    
    case 'react-vite':
    case 'react-cra':
      return {
        ...baseConfig,
        compilerOptions: {
          ...baseConfig.compilerOptions,
          lib: ['dom', 'dom.iterable', 'es6'],
          noEmit: true,
          module: 'esnext',
          moduleResolution: 'bundler',
          jsx: 'react-jsx',
          ...(config.enablePathMapping && {
            baseUrl: '.',
            paths: FileOperations.getDynamicPathMappings(config.projectInfo || { framework, rootDir: process.cwd() })
          })
        },
        include: ['src/**/*'],
        exclude: ['node_modules', 'dist', 'build']
      };
    
    case 'vue':
      return {
        ...baseConfig,
        compilerOptions: {
          ...baseConfig.compilerOptions,
          lib: ['dom', 'dom.iterable', 'es6'],
          noEmit: true,
          module: 'esnext',
          moduleResolution: 'bundler',
          jsx: 'preserve',
          ...(config.enablePathMapping && {
            baseUrl: '.',
            paths: FileOperations.getDynamicPathMappings(config.projectInfo || { framework, rootDir: process.cwd() })
          })
        },
        include: ['src/**/*'],
        exclude: ['node_modules', 'dist', 'build']
      };
    
    case 'angular':
      return {
        ...baseConfig,
        compilerOptions: {
          ...baseConfig.compilerOptions,
          lib: ['dom', 'dom.iterable', 'es6'],
          noEmit: true,
          module: 'esnext',
          moduleResolution: 'bundler',
          jsx: 'preserve',
          ...(config.enablePathMapping && {
            baseUrl: '.',
            paths: {
              '@/*': ['./src/*'],
              '@/components/*': ['./src/app/components/*'],
              '@/lib/*': ['./src/lib/*']
            }
          })
        },
        include: ['src/**/*'],
        exclude: ['node_modules', 'dist', 'build']
      };
    
    case 'svelte':
      return {
        ...baseConfig,
        compilerOptions: {
          ...baseConfig.compilerOptions,
          lib: ['dom', 'dom.iterable', 'es6'],
          noEmit: true,
          module: 'esnext',
          moduleResolution: 'bundler',
          jsx: 'preserve',
          ...(config.enablePathMapping && {
            baseUrl: '.',
            paths: {
              '@/*': ['./src/*'],
              '@/components/*': ['./src/lib/*'],
              '@/lib/*': ['./src/lib/*']
            }
          })
        },
        include: ['src/**/*'],
        exclude: ['node_modules', 'dist', 'build']
      };
    
    case 'express':
    case 'fastify':
    case 'nodejs':
    default:
      return {
        ...baseConfig,
        compilerOptions: {
          ...baseConfig.compilerOptions,
          lib: ['es2022'],
          noEmit: false,
          module: 'commonjs',
          moduleResolution: 'node',
          outDir: './dist',
          rootDir: './src',
          ...(config.enablePathMapping && {
            baseUrl: '.',
            paths: {
              '@/*': ['./src/*'],
              '@/lib/*': ['./src/lib/*'],
              '@/types/*': ['./src/types/*']
            }
          })
        },
        include: ['src/**/*'],
        exclude: ['node_modules', 'dist']
      };
  }
}

function getStrictnessOptions(level: string): Record<string, any> {
  switch (level) {
    case 'strict':
      return {
        strict: true,
        noImplicitAny: true,
        strictNullChecks: true,
        noImplicitReturns: true,
        noImplicitThis: true,
        alwaysStrict: true,
        noUnusedLocals: true,
        noUnusedParameters: true
      };
    case 'recommended':
      return {
        strict: true,
        noImplicitAny: true,
        strictNullChecks: true,
        noImplicitReturns: true
      };
    case 'custom':
    default:
      return {
        strict: false,
        noImplicitAny: false,
        strictNullChecks: false,
        noImplicitReturns: false
      };
  }
}

// Standalone functions for direct package usage
export async function setupTypescript(config: Partial<ScaffoldConfig> = {}): Promise<InstallResult> {
  const scaffoldConfig: ScaffoldConfig = {
    projectInfo: {
      rootDir: config.projectInfo?.rootDir || process.cwd(),
      packageManager: config.projectInfo?.packageManager || 'npm'
    },
    framework: config.framework || 'nodejs',
    options: config.options || {}
  };
  
  return await scaffold.install(scaffoldConfig);
}

export async function previewTypescript(config: Partial<ScaffoldConfig> = {}): Promise<FileChange[]> {
  const scaffoldConfig: ScaffoldConfig = {
    projectInfo: {
      rootDir: config.projectInfo?.rootDir || process.cwd(),
      packageManager: config.projectInfo?.packageManager || 'npm'
    },
    framework: config.framework || 'nodejs',
    options: config.options || {}
  };
  
  return await scaffold.getPreview!(scaffoldConfig);
}

export default scaffold;
